import React from 'react';
import { createPortal } from 'react-dom';
import { WidgetLiveRegion, type WidgetLivePoliteness } from './WidgetLiveRegion';

export interface AnnounceOptions {
  politeness?: WidgetLivePoliteness;
  /** When set, the live region gets `lang` for this message only. */
  lang?: string;
}

type QueuedAnnouncement = {
  message: string;
  lang?: string;
};

interface WidgetAnnouncerContextValue {
  announce: (message: string, options?: AnnounceOptions) => void;
  registerOutlet: (node: HTMLElement | null) => void;
}

const WidgetAnnouncerContext = React.createContext<WidgetAnnouncerContextValue | null>(null);

/**
 * How long a message owns its live region before the next queued one replaces
 * it. Writing straight over a region (as this provider used to) meant two
 * announcements in the same tick left only the last one for the screen reader.
 */
export const ANNOUNCEMENT_DWELL_MS = 1200;

/**
 * Serialises messages for a single live region. The region is blanked between
 * messages so repeating the same text still registers as a content change —
 * screen readers only speak when the content actually changes.
 */
function useLiveRegionQueue() {
  const [queue, setQueue] = React.useState<QueuedAnnouncement[]>([]);
  const [current, setCurrent] = React.useState<QueuedAnnouncement | null>(null);

  const push = React.useCallback((item: QueuedAnnouncement) => {
    setQueue((pending) => [...pending, item]);
  }, []);

  React.useEffect(() => {
    if (current !== null || queue.length === 0) return;

    setCurrent(queue[0]);
    setQueue((pending) => pending.slice(1));
  }, [current, queue]);

  React.useEffect(() => {
    if (current === null) return;

    const timer = window.setTimeout(() => setCurrent(null), ANNOUNCEMENT_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [current]);

  return {
    message: current?.message ?? '',
    lang: current?.lang,
    push,
  };
}

export function WidgetAnnouncerProvider({ children }: { children: React.ReactNode }) {
  const polite = useLiveRegionQueue();
  const assertive = useLiveRegionQueue();
  const { push: pushPolite } = polite;
  const { push: pushAssertive } = assertive;

  const announce = React.useCallback(
    (message: string, options?: AnnounceOptions) => {
      const item: QueuedAnnouncement = {
        message,
        ...(options?.lang ? { lang: options.lang } : {}),
      };
      if ((options?.politeness ?? 'polite') === 'assertive') {
        pushAssertive(item);
        return;
      }
      pushPolite(item);
    },
    [pushAssertive, pushPolite],
  );

  const [outlet, setOutlet] = React.useState<HTMLElement | null>(null);
  const registerOutlet = React.useCallback((node: HTMLElement | null) => setOutlet(node), []);

  const value = React.useMemo(
    () => ({ announce, registerOutlet }),
    [announce, registerOutlet],
  );

  // Separate regions per politeness: an alert must never wait behind polite
  // chatter, and a region's politeness cannot be changed after a screen reader
  // has already picked it up.
  const regions = (
    <>
      <WidgetLiveRegion
        message={polite.message}
        politeness="polite"
        lang={polite.lang}
      />
      <WidgetLiveRegion
        message={assertive.message}
        politeness="assertive"
        lang={assertive.lang}
      />
    </>
  );

  return (
    <WidgetAnnouncerContext.Provider value={value}>
      {children}
      {/* The open chat panel is `aria-modal`, which tells assistive tech to
          ignore everything outside it. Regions left out here are therefore
          never spoken while the panel is open, so they relocate into the
          panel whenever one registers an outlet. */}
      {outlet ? createPortal(regions, outlet) : regions}
    </WidgetAnnouncerContext.Provider>
  );
}

/**
 * Placed inside the modal panel so announcements stay within the subtree that
 * assistive tech is allowed to see.
 */
export function WidgetAnnouncerOutlet() {
  const { registerOutlet } = useWidgetAnnouncer();
  return <div ref={registerOutlet} />;
}

export function useWidgetAnnouncer(): WidgetAnnouncerContextValue {
  const context = React.useContext(WidgetAnnouncerContext);
  if (!context) {
    throw new Error('useWidgetAnnouncer must be used within WidgetAnnouncerProvider');
  }
  return context;
}
